import { IconCheck, IconCircleDashed } from "@tabler/icons-react";
import { Badge, Card, Divider, Group, Heading, Loader, Stack, Text, ThemeIcon } from "@/components/ui";
import type { CategoriaDocumento, ChecklistDocumentos, DocumentoVinculado } from "@/lib/types";
import { DocumentoRow } from "./DocumentoRow";

interface Props {
  checklist: ChecklistDocumentos;
  pendientesXml: number;
  pendientesPdf: number;
  pendientesAdicional: number;
  pendientesCertificado?: number;
  solicitudId: string;
  /** P44 · FB60 Contrapago: no hay CFDI todavía · oculta Factura XML/PDF del resumen. */
  esContrapago?: boolean;
  /** Invoice: PDF del Invoice (ya cargado) + certificado de residencia + Adicional. */
  esInvoice?: boolean;
}

interface ItemDef {
  label: string;
  ok: boolean;
  qty: number;
  pendientes: number;
  /** Documentos de esta categoría con acciones (ver/descargar/eliminar). */
  docs: DocumentoVinculado[];
}

/**
 * Resumen del checklist FB60 · pinta los 3 obligatorios con estado
 * (vacío/completo/pendiente). Para Factura XML y Factura PDF (que provienen
 * del paso CFDI) lista además los archivos con sus acciones · ya no hay una
 * tarjeta de carga aparte para ellos. "Adicional" se sube en su propia tarjeta.
 */
export function ChecklistResumen({ checklist, pendientesXml, pendientesPdf, pendientesAdicional, pendientesCertificado = 0, solicitudId, esContrapago = false, esInvoice = false }: Props) {
  const docsDe = (cat: CategoriaDocumento) => checklist.documentos.filter((d) => d.categoria === cat);

  // Ítems obligatorios ANTES de "Documentación Adicional", según el subtipo.
  const itemsPrevios: ItemDef[] = esInvoice
    ? [
        // El PDF del Invoice ya se cargó en el paso Factura (PDF) · siempre satisfecho aquí.
        { label: "PDF del Invoice", ok: true, qty: 1, pendientes: 0, docs: [] },
        { label: "Certificado de residencia fiscal", ok: checklist.certificadoResidenciaCargados >= 1, qty: checklist.certificadoResidenciaCargados, pendientes: pendientesCertificado, docs: docsDe("CertificadoResidencia") },
      ]
    : esContrapago
      ? // P44 · Contrapago no tiene CFDI todavía (llega después del pago) · sin Factura XML/PDF.
        []
      : [
          { label: "Factura XML", ok: checklist.facturaXmlCargados >= 1, qty: checklist.facturaXmlCargados, pendientes: pendientesXml, docs: docsDe("FacturaXml") },
          { label: "Factura PDF", ok: checklist.facturaPdfCargados >= 1, qty: checklist.facturaPdfCargados, pendientes: pendientesPdf, docs: docsDe("FacturaPdf") },
        ];

  const items: ItemDef[] = [
    ...itemsPrevios,
    {
      label: "Documentación Adicional",
      ok: checklist.adicionalesCargados >= 1,
      qty: checklist.adicionalesCargados,
      pendientes: pendientesAdicional,
      // "Documentación Adicional" se gestiona en su propia tarjeta · aquí solo resumen.
      docs: [],
    },
  ];

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap={6} align="baseline">
              <Heading order={5}>Checklist {esInvoice ? "Invoice" : esContrapago ? "Contrapago" : "FB60"}</Heading>
              <Badge variant="light" color={checklist.completoFB60 ? "multiva" : "advertencia"}>
                {checklist.completoFB60 ? "Completo" : "Incompleto"}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed" className="mt-0.5">
              {esInvoice
                ? "PDF del Invoice (ya cargado) + certificado de residencia fiscal (1) + ≥1 Adicional."
                : esContrapago
                  ? "P44 · ≥1 documento Adicional (el CFDI llega después del pago)."
                  : "Decisión 10 · ≥1 Factura XML + ≥1 Factura PDF + ≥1 Adicional."}
            </Text>
          </div>
        </Group>
        <Divider />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {items.map((it) => (
            <ChecklistItemCard key={it.label} {...it} solicitudId={solicitudId} />
          ))}
        </div>
        {checklist.otrosCargados > 0 && (
          <Text size="xs" c="dimmed">
            Otros documentos cargados (Evidencia/Contrato): {checklist.otrosCargados}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function ChecklistItemCard({ label, ok, qty, pendientes, docs, solicitudId }: ItemDef & { solicitudId: string }) {
  const tienePendientes = pendientes > 0;
  const color = tienePendientes ? "info" : ok ? "multiva" : "advertencia";
  return (
    <div
      className="rounded-lg p-2.5"
      style={{ border: `1px solid var(--color-${color}-300)`, background: `color-mix(in srgb, var(--color-${color}-600) 6%, transparent)` }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap" className="min-w-0">
          {tienePendientes ? (
            <Loader size="xs" color="multiva" />
          ) : (
            <ThemeIcon size="sm" variant="filled" color={color}>
              {ok ? <IconCheck size={12} stroke={2} /> : <IconCircleDashed size={12} stroke={1.7} />}
            </ThemeIcon>
          )}
          <Text size="sm" fw={600} truncate>
            {label}
          </Text>
        </Group>
        <Group gap={4} wrap="nowrap">
          {/* Acciones ver/descargar en la misma línea (solo XML/PDF) · el
              nombre del archivo va en tooltip. */}
          {docs.map((d) => (
            <DocumentoRow key={d.id} documento={d} solicitudId={solicitudId} puedeEliminar={false} compacto />
          ))}
          <Text size="xs" c="dimmed">
            ({qty})
            {tienePendientes && (
              <Text span c={`${color}-700`} fw={600}>
                {" "}
                +{pendientes}…
              </Text>
            )}
          </Text>
        </Group>
      </Group>
    </div>
  );
}
